import React from 'react';
import {
  checkDownloadComplete,
  fetchExportDownloadList,
  findProgressItems,
  ExportDownloadItem,
  fetchProgressItems,
  getFinalPollState
} from 'src/components/BeaconV2Components/common/AppExportModal/exportDataUtils';
import { useAppSelector } from 'src/utils/Hooks';
import _isEqual from 'lodash/isEqual';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Link } from 'react-router-dom';

export const TIME_OUT_PERIOD = 5000;

export enum POLL_STATE {
  STOPPED,
  RUNNING,
  SUCCESS,
  ERROR
}

const SuccessSnackbarMessage = () => {
  const { queryParams } = useAppSelector((state) => state.app);
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Text variant="subtitle2">
        Your download is complete. View all downloads{' '}
        <Link to={`/account/downloads${queryParams.searchParams}`} style={{ textDecoration: 'underline' }}>
          here
        </Link>
        .
      </Text>
    </div>
  );
};

type PollerFunction = (
  setDownloadList: React.Dispatch<React.SetStateAction<ExportDownloadItem[]>>,
  setPollState: React.Dispatch<React.SetStateAction<POLL_STATE>>,
  setNotificationBadge: (state: boolean) => void,
  showSnackbar: ({ message, type }: { message: string; type: 'success' | 'error' }) => void
) => Promise<void>;

class ProgressPoller {
  private static instance: ProgressPoller;

  private isPolling: boolean;

  private downloadList: ExportDownloadItem[];

  private constructor() {
    this.isPolling = false;
  }

  public static getInstance(): ProgressPoller {
    if (!ProgressPoller.instance) {
      ProgressPoller.instance = new ProgressPoller();
    }
    return ProgressPoller.instance;
  }

  public setDownloadList(downloadList: ExportDownloadItem[]): void {
    this.downloadList = downloadList;
  }

  public fetchProgressItemsAndReplace: PollerFunction = async (
    setDownloadList,
    setPollState,
    setNotificationBadge,
    showSnackbar
  ) => {
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;

    try {
      const progressItems = findProgressItems(this.downloadList);
      const progressItemsIds = progressItems.map((item) => item.requestId);

      if (progressItemsIds.length === 0) {
        return;
      }

      const newlyFetchedProgressItems = await fetchProgressItems(progressItemsIds);
      let hasChanges = false;

      const updatedDownloadList = this.downloadList?.map((item) => {
        const newItem = newlyFetchedProgressItems.find((fetchedItem) => fetchedItem.requestId === item.requestId);
        if (newItem && !_isEqual(item, newItem)) {
          hasChanges = true;
          if (newItem.status === 'finished successfully') {
            showSnackbar({ message: <SuccessSnackbarMessage />, type: 'success' });
            setNotificationBadge(true);
          } else if (newItem.status === 'failed with error') {
            showSnackbar({ message: 'Export failed', type: 'error' });
          }
          return newItem;
        }
        return item;
      });

      if (hasChanges) {
        setDownloadList(updatedDownloadList);
      }

      if (!checkDownloadComplete(newlyFetchedProgressItems)) {
        setTimeout(
          () => this.fetchProgressItemsAndReplace(setDownloadList, setPollState, setNotificationBadge, showSnackbar),
          TIME_OUT_PERIOD
        );
      } else {
        setPollState(getFinalPollState(updatedDownloadList));
      }
    } catch (error) {
      console.error('Error while fetching progress items:', error);
    } finally {
      this.isPolling = false;
    }
  };
}

export const progressPoller = ProgressPoller.getInstance();

export const fetchProgressItemsAndReplace = progressPoller.fetchProgressItemsAndReplace.bind(progressPoller);
export const updateDownloadList = progressPoller.setDownloadList.bind(progressPoller);

export const initializeDownloadList = async (
  setIsDownloadLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setDownloadList: React.Dispatch<React.SetStateAction<ExportDownloadItem[]>>
) => {
  try {
    setIsDownloadLoading(true);
    const { result: fetchedDownloadList } = await fetchExportDownloadList();
    if (fetchedDownloadList) {
      setDownloadList(fetchedDownloadList);
    }
  } catch (error) {
    console.error('Error while initializing download list:', error);
  } finally {
    setIsDownloadLoading(false);
  }
};
